import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Row, Col } from 'reactstrap';
import { addData, updateData } from '../../lib/courselesson';
import { CourseDepartmentSelect, CourseLevelSelect, CourseApScoreSelect, ClassForTranskriptSelect } from '../Common/FormSelects';

const LessonModal = ({ isOpen, toggle, studentId, lessonToEdit, onSave }) => {
    const [coursename, setCoursename] = useState('');
    const [department, setDepartment] = useState('English');
    const [level, setLevel] = useState('AP');
    const [apscore, setApscore] = useState(0);
    const [grade, setGrade] = useState('');
    const [credit, setCredit] = useState('');
    const [courseyear, setCourseyear] = useState(9);
    const [isgpacalc, setIsgpacalc] = useState(true);
    const [autograde, setAutograde] = useState(false);
    const [loading, setLoading] = useState(false);

    const isEdit = !!lessonToEdit;

    useEffect(() => {
        if (isOpen) {
            if (isEdit) {
                setCoursename(lessonToEdit.coursename || '');
                setDepartment(lessonToEdit.department || 'English');
                setLevel(lessonToEdit.level || 'AP');
                setApscore(lessonToEdit.apscore || 0);
                setGrade(lessonToEdit.grade || '');
                setCredit(lessonToEdit.credit || '');
                setCourseyear(lessonToEdit.courseyear || 9);
                setIsgpacalc(lessonToEdit.isgpacalc !== undefined ? lessonToEdit.isgpacalc : true);
                setAutograde(lessonToEdit.autograde || false);
            } else {
                setCoursename('');
                setDepartment('English');
                setLevel('AP');
                setApscore(0);
                setGrade('');
                setCredit('');
                setCourseyear(9);
                setIsgpacalc(true);
                setAutograde(false);
            }
        }
    }, [isOpen, lessonToEdit]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const payload = {
                coursename,
                department,
                level,
                apscore: level === 'AP' ? apscore : 0,
                grade: parseFloat(grade) || 0,
                credit: parseFloat(credit) || 0,
                courseyear,
                isgpacalc,
                autograde,
                studentId
            };

            if (isEdit) {
                await updateData({ ...payload, id: lessonToEdit.id });
            } else {
                await addData(payload);
            }
            onSave(isEdit ? 'Course updated successfully!' : 'Course added successfully!');
            toggle();
        } catch (error) {
            console.error('Error saving lesson:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {isEdit ? 'Update Lesson' : 'Add New Lesson'}
            </ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit} id="lesson-form">
                    <Row>
                        <Col md="12">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Course Name</Label>
                                <Input
                                    type="text"
                                    value={coursename}
                                    onChange={(e) => setCoursename(e.target.value)}
                                    placeholder="Enter Course Name"
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Department</Label>
                                <CourseDepartmentSelect value={department} setValue={setDepartment} />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Course Level</Label>
                                <CourseLevelSelect value={level} setValue={setLevel} />
                            </FormGroup>
                        </Col>
                        {level === 'AP' && (
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label className="fw-bold">AP Test Score</Label>
                                    <CourseApScoreSelect value={apscore} setValue={setApscore} />
                                </FormGroup>
                            </Col>
                        )}
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Grade</Label>
                                <Input
                                    type="number"
                                    value={grade}
                                    onChange={(e) => setGrade(e.target.value)}
                                    placeholder="Enter Grade (0-100)"
                                    min="0"
                                    max="100"
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Credit</Label>
                                <Input
                                    type="number"
                                    step="0.25"
                                    value={credit}
                                    onChange={(e) => setCredit(e.target.value)}
                                    placeholder="Enter Credit"
                                    min="0"
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">High School Year</Label>
                                <ClassForTranskriptSelect value={courseyear} setValue={setCourseyear} />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">GPA Calculation</Label>
                                <div className="d-flex gap-3">
                                    <div className="form-check">
                                        <Input
                                            className="form-check-input"
                                            type="radio"
                                            name="isgpacalc"
                                            id="gpaYes"
                                            checked={isgpacalc}
                                            onChange={() => setIsgpacalc(true)}
                                        />
                                        <Label className="form-check-label" for="gpaYes">Yes</Label>
                                    </div>
                                    <div className="form-check">
                                        <Input
                                            className="form-check-input"
                                            type="radio"
                                            name="isgpacalc"
                                            id="gpaNo"
                                            checked={!isgpacalc}
                                            onChange={() => setIsgpacalc(false)}
                                        />
                                        <Label className="form-check-label" for="gpaNo">No</Label>
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Auto Grade</Label>
                                <div className="form-check form-switch">
                                    <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="autoGradeSwitch"
                                        checked={autograde}
                                        onChange={(e) => setAutograde(e.target.checked)}
                                    />
                                    <Label className="form-check-label" for="autoGradeSwitch">
                                        Enable Auto Grade
                                    </Label>
                                </div>
                                <small className="text-muted">
                                    If enabled, the grade will be automatically pulled from GPA-Grades.
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" type="submit" form="lesson-form" disabled={loading}>
                    <i className="fa fa-save me-2"></i>
                    {loading ? 'Saving...' : isEdit ? 'Update Course' : 'Add Course'}
                </Button>
                <Button color="secondary" onClick={toggle} disabled={loading}>
                    <i className="fa fa-times me-2"></i>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default LessonModal;
